import { useWorkspace } from '@/features/workspaces/hooks';
import { GeoStreetRef } from '@/models/audiences';
import { useGetGeoStreetsQuery } from '@/services/endpoints/geo';
import { FC, lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuInfo, LuX } from 'react-icons/lu';
import { twMerge } from 'tailwind-merge';
import { GeoZone } from '../../expression';
import { useNewCommunicationActions } from '../context/useNewCommunicationActions';
import { useNewCommunicationValues } from '../context/useNewCommunicationValues';
import { StreetPicker } from './StreetPicker';

type Tab = 'streets' | 'map';

// the Azure Maps SDK is heavy and only needed on the map tab
const PolygonMap = lazy(() => import('./PolygonMap').then((m) => ({ default: m.PolygonMap })));

const CHIP_STREETS = 3;

const sameRing = (a: number[][] | null, b: number[][] | null) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((point, index) => point[0] === b[index][0] && point[1] === b[index][1]);
};

/** The confirmed zone as a removable chip; clicking the label reopens the panel. */
export const GeoZoneChip: FC<{ zone: GeoZone; onOpen: () => void; onRemove: () => void }> = ({ zone, onOpen, onRemove }) => {
  const { t } = useTranslation('comuni');

  const label =
    zone.kind === 'polygon'
      ? t('composer.geo.chipArea')
      : t('composer.geo.chipStreets', {
          streets:
            zone.streets
              .slice(0, CHIP_STREETS)
              .map((s) => s.street.toLowerCase())
              .join(', ') + (zone.streets.length > CHIP_STREETS ? ` +${zone.streets.length - CHIP_STREETS}` : ''),
        });
  return (
    <div className='mx-5 mb-3 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3.5 py-1.5'>
      <button type='button' onClick={onOpen} className='text-[13px] font-bold capitalize text-brand-700'>
        {label}
      </button>
      <button type='button' onClick={onRemove} aria-label={t('composer.removeFilter')} className='text-brand-700 hover:text-red-600'>
        <LuX className='size-3.5' strokeWidth={3} />
      </button>
    </div>
  );
};

type GeoZonePanelProps = { zone: GeoZone | null; onConfirm: (zone: GeoZone) => void; onClose: () => void };

/** "Seleziona zona": pick streets from the list or draw one polygon on the map, then confirm. */
export const GeoZonePanel: FC<GeoZonePanelProps> = ({ zone, onConfirm, onClose }) => {
  const { t } = useTranslation('comuni');
  const { id: workspaceId } = useWorkspace();
  const { geo } = useNewCommunicationValues();
  const { estimateZone } = useNewCommunicationActions();

  const [tab, setTab] = useState<Tab>(zone?.kind === 'polygon' ? 'map' : 'streets');
  const [streets, setStreets] = useState<GeoStreetRef[]>(zone?.kind === 'streets' ? zone.streets : []);
  const [polygon, setPolygon] = useState<number[][] | null>(zone?.kind === 'polygon' ? zone.polygon : null);
  /** Citizens inside the drawn area: undefined while unknown, null when the API rejected the ring (self-intersecting). */
  const [inside, setInside] = useState<number | null | undefined>(undefined);

  const ready = geo.status === 'ready';
  const streetsQuery = useGetGeoStreetsQuery({ workspaceId, datasourceIds: geo.qualifyingIds }, { skip: !ready || tab !== 'streets' });

  const updatePolygon = (next: number[][] | null) => {
    setPolygon((current) => (sameRing(current, next) ? current : next));
  };

  // live count of the drawn area, debounced: one estimate with the GEO rule alone
  useEffect(() => {
    setInside(undefined);
    if (!polygon || !ready) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      estimateZone({ kind: 'polygon', polygon }).then(
        (size) => !cancelled && setInside(size),
        (error: { status?: unknown }) => !cancelled && error?.status === 400 && setInside(null)
      );
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [polygon, ready, estimateZone]);

  // the map needs a completed estimate: while it is pending the ring may still turn out invalid
  const canConfirm = ready && (tab === 'streets' ? streets.length > 0 : (polygon?.length ?? 0) >= 3 && typeof inside === 'number');
  const confirm = () => {
    if (!canConfirm) return;
    onConfirm(tab === 'streets' ? { kind: 'streets', streets } : { kind: 'polygon', polygon: polygon! });
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'streets', label: t('composer.geo.tabStreets') },
    { key: 'map', label: t('composer.geo.tabMap') },
  ];

  return (
    <div className='mx-5 mb-4 rounded-xl border bg-white-100 p-4'>
      <div className='flex items-center justify-between gap-3'>
        <div role='tablist' className='inline-flex overflow-hidden rounded-lg border'>
          {tabs.map((item) => (
            <button
              key={item.key}
              type='button'
              role='tab'
              aria-selected={tab === item.key}
              onClick={() => setTab(item.key)}
              className={twMerge(
                'px-4 py-2 text-sm font-semibold transition-colors',
                tab === item.key ? 'bg-brand-700 text-white-100' : 'bg-white-100 text-secondary-700 hover:text-brand-700'
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
        <button type='button' onClick={onClose} aria-label={t('composer.geo.cancel')} className='p-1 text-quaternary-400 hover:text-primary-900'>
          <LuX className='size-4' />
        </button>
      </div>

      {geo.status === 'loading' && <p className='mt-4 text-[13.5px] text-quaternary-400'>{t('composer.geo.loading')}</p>}
      {(geo.status === 'unavailable' || geo.status === 'processing') && (
        <div className='mt-4 flex items-start gap-2 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-[13.5px] leading-relaxed text-tertiary-600'>
          <LuInfo className='mt-0.5 size-4 flex-none text-yellow-700' />
          <span>
            {geo.status === 'unavailable'
              ? t('composer.geo.unavailable')
              : geo.pending > 0
                ? t('composer.geo.processing', { pending: geo.pending })
                : t('composer.geo.processingNoCount')}
          </span>
        </div>
      )}

      {ready && (
        <div className='mt-4 grid gap-3'>
          <p className='text-[12.5px] text-tertiary-600'>
            {tab === 'map'
              ? t('composer.geo.onMap', { onMap: geo.onMap, total: geo.total })
              : t('composer.geo.withStreet', { located: geo.withStreet, total: geo.total })}
          </p>
          {tab === 'streets' ? (
            <StreetPicker streets={streetsQuery.currentData} isLoading={streetsQuery.isFetching} selected={streets} onChange={setStreets} />
          ) : (
            <Suspense fallback={<p className='text-[13.5px] text-quaternary-400'>{t('composer.geo.loading')}</p>}>
              <PolygonMap polygon={polygon} onChange={updatePolygon} inside={inside} bounds={geo.bounds} />
            </Suspense>
          )}
        </div>
      )}

      <div className='mt-4 flex justify-end gap-2.5 border-t pt-4'>
        <button
          type='button'
          onClick={onClose}
          className='rounded-[9px] border bg-white-100 px-4 py-2 text-[13.5px] font-bold text-primary-900 hover:bg-secondary-background'
        >
          {t('composer.geo.cancel')}
        </button>
        <button
          type='button'
          onClick={confirm}
          disabled={!canConfirm}
          className={`rounded-[9px] px-5 py-2 text-[13.5px] font-bold ${canConfirm ? 'bg-brand-700 text-white-100 hover:bg-brand-800' : 'cursor-default bg-quaternary-100 text-quaternary-400'}`}
        >
          {t('composer.geo.confirm')}
        </button>
      </div>
    </div>
  );
};
